import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getBestSellerProducts } from "../services/productService";

const IMG_BASE =
  import.meta.env.VITE_IMG_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const price = 20 + (p.name.charCodeAt(0) % 50);
  const oldPrice = price + 10;
  const primary = p.images?.find((img) => img.is_primary) || p.images?.[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = p.images?.length > 1 ? p.images[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category?.name || "",
    category_id: p.category_id,
    price,
    oldPrice,
    img1,
    img2,
    description: p.description,
    additional_information: p.additional_information,
    status: p.status,
    images: p.images || [],
  };
};

// Split an array into chunks of a given size
const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

function SmallProductCard({ product }) {
  return (
    <div className="flex gap-3 items-center py-3 border-b border-border-color group">
      <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-gray-50 relative">
        <img
          src={product.img1}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
        <img
          src={product.img2}
          alt={product.name}
          className="w-full h-full object-cover absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-dark hover:text-primary cursor-pointer truncate">
          <Link to={`/product-details/${product.product_id || product.id}`}>
            {product.name}
          </Link>
        </h4>
        <p className="text-xs text-gray-400 mt-0.5">
          <Link to="/products" className="hover:text-primary">
            {product.category}
          </Link>
        </p>

        {/* <div className="mt-1 flex gap-2 items-center">
          <span className="text-primary text-sm font-bold">
            ₹{product.price}.00
          </span>
          <span className="text-gray-400 line-through text-xs">
            ₹{product.oldPrice}.00
          </span>
        </div> */}
      </div>
    </div>
  );
}

export default function BestSellers() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const fetchProducts = async () => {
      try {
        const res = await getBestSellerProducts();
        const data = res.data?.data || res.data || [];
        if (isMounted) {
          setProducts(data.map(mapApiProduct));
        }
      } catch {
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchProducts();
    return () => {
      isMounted = false;
    };
  }, []);

  // Each page shows 3 columns x 3 products = 9 products per page
  const pages = chunkArray(products, 9);

  if (loading) {
    return (
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="section-title mb-8">
            <p>Recently added our store</p>
            <h2>Best Sellers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Banner Skeleton */}
            <div className="md:col-span-1">
              <div className="w-full h-80 bg-gray-200 animate-pulse rounded"></div>
            </div>

            {/* Products Skeleton Grid */}
            <div className="md:col-span-2 lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[0, 1, 2].map((col) => (
                  <div key={col} className="space-y-3 [&>*:nth-child(n+3)]:hidden sm:[&>*:nth-child(n+3)]:block">
                    {[0, 1, 2].map((item) => (
                      <div key={item} className="flex gap-3 items-center py-3 border-b border-border-color animate-pulse">
                        <div className="w-20 h-20 bg-gray-200 flex-shrink-0 rounded"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="section-title mb-8">
          <p>Recently added our store</p>
          <h2>Best Sellers</h2>
        </div>

        {products.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-10">
            No best seller products found.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* Banner */}
            <div className="md:col-span-1">
              <Link to="/products">
                <img
                  src={`${process.env.PUBLIC_URL}/img/slider/Gemini_Generated_Image_pmmc6cpmmc6cpmmc.png`}
                  alt="banner"
                  className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                />
              </Link>
            </div>

            {/* Products */}
            <div className="md:col-span-2 lg:col-span-3">
              {/* Navigation arrows — only show when there are more products than one page */}
              <div className="relative flex items-center">
                {/* Left Arrow */}
                <button
                  onClick={() => setPage((i) => Math.max(0, i - 1))}
                  disabled={page === 0}
                  className="absolute -left-2 md:-left-6 z-10 w-8 h-8 rounded-full bg-white/90 md:bg-transparent shadow-sm md:shadow-none flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
                  aria-label="Previous"
                >
                  <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                    <path d="M9 1L1 9L9 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <div className="overflow-hidden w-full">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {chunkArray(pages[page] || [], 3).map((column, colIndex) => (
                     <div key={colIndex} className="space-y-0 [&>*:nth-child(n+3)]:hidden sm:[&>*:nth-child(n+3)]:block">

                        {column.map((prod) => (
                          <SmallProductCard key={prod.id} product={prod} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Arrow */}
                <button
                  onClick={() => setPage((i) => Math.min(pages.length - 1, i + 1))}
                  disabled={page >= pages.length - 1}
                  className="absolute -right-2 md:-right-6 z-10 w-8 h-8 rounded-full bg-white/90 md:bg-transparent shadow-sm md:shadow-none flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
                  aria-label="Next"
                >
                  <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                    <path d="M1 1L9 9L1 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}